import { useState } from 'react'
import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  cpu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="14" x2="23" y2="14" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="14" x2="4" y2="14" />
    </svg>
  ),
  radio: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" />
      <path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" />
    </svg>
  ),
  thermometer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  sun: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  )
}

const features = [
  {
    icon: Icons.activity,
    title: '12 String Monitoring',
    description: 'Individual voltage, current, and power measurement for each panel string'
  },
  {
    icon: Icons.radio,
    title: 'LoRa Mesh Network',
    description: 'Long-range wireless connectivity up to 10km with mesh networking capability'
  },
  {
    icon: Icons.thermometer,
    title: 'Temperature Sensing',
    description: 'Dual DS18B20 probes for internal enclosure and ambient temperature monitoring'
  },
  {
    icon: Icons.shield,
    title: 'IP67 Weatherproof',
    description: 'Industrial-grade enclosure rated for outdoor installation in any climate'
  },
  {
    icon: Icons.zap,
    title: 'Wide Voltage Input',
    description: '12-24V DC input with integrated buck converter for reliable operation'
  },
  {
    icon: Icons.cpu,
    title: 'Edge AI Processing',
    description: 'On-device anomaly detection with XIAO ESP32S3 microcontroller'
  }
]

const specs = [
  { label: 'Monitoring Channels', value: '12 strings' },
  { label: 'Voltage Range', value: '0-60V per string' },
  { label: 'Current Range', value: '0-20A per string' },
  { label: 'Wireless Range', value: 'Up to 10km LoRa' },
  { label: 'Update Interval', value: '5 minutes' },
  { label: 'Enclosure', value: 'IP67 200x150x100mm' },
  { label: 'Power Input', value: '12-24V DC' },
  { label: 'Temperature Range', value: '-40C to +85C' }
]

export default function SensorBox() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    // In production, this would submit to an email service
    setSubmitted(true)
  }

  return (
    <div className="draft-page sensor-box-page">
      {/* Hero */}
      <section className="draft-hero sensor-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge coming-soon-badge">Coming Q2 2026</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              <GradientText gradient="energy">SMM Sensor Box</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              String-level solar monitoring with LoRa mesh connectivity.
              Industrial-grade hardware designed for the AI4Solar platform.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="hero-visual">
              <motion.div
                className="sensor-box-preview"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                <div className="box-illustration">
                  <div className="box-body">
                    <div className="box-face front">
                      <div className="box-label">SMM-Basic</div>
                      <div className="box-indicators">
                        <span className="indicator power"></span>
                        <span className="indicator data"></span>
                        <span className="indicator lora"></span>
                      </div>
                    </div>
                    <div className="box-face top">
                      <div className="antenna-mount"></div>
                    </div>
                    <div className="box-face right"></div>
                  </div>
                  <div className="signal-waves">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Features Grid */}
      <section className="draft-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Hardware Features</h2>
            <p>Purpose-built for commercial solar installations</p>
          </ScrollReveal>

          <div className="features-grid sensor-features">
            {features.map((feature, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="feature-card"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <span className="feature-icon">{feature.icon}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="draft-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Technical Specifications</h2>
            <p>Enterprise-grade components for reliable operation</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="specs-grid">
              {specs.map((spec, i) => (
                <motion.div
                  key={i}
                  className="spec-item"
                  whileHover={{ scale: 1.02 }}
                >
                  <span className="spec-label">{spec.label}</span>
                  <span className="spec-value">{spec.value}</span>
                </motion.div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="pricing-preview">
              <div className="price-card">
                <span className="price-label">Starting at</span>
                <span className="price-value">$210</span>
                <span className="price-note">per unit (SMM-Basic)</span>
              </div>
              <div className="price-variants">
                <div className="variant">
                  <span className="variant-name">SMM-Basic</span>
                  <span className="variant-desc">12 strings, LoRa, 2 temp sensors</span>
                </div>
                <div className="variant">
                  <span className="variant-name">SMM-Thermal</span>
                  <span className="variant-desc">+ Thermal camera integration</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Integration */}
      <section className="draft-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Seamless Integration</h2>
            <p>Designed to work with the AI4Solar platform</p>
          </ScrollReveal>

          <div className="integration-flow">
            <ScrollReveal delay={0.1}>
              <motion.div className="flow-step" whileHover={{ y: -5 }}>
                <span className="step-icon">{Icons.box}</span>
                <span className="step-title">Sensor Box</span>
                <span className="step-desc">Collects string data</span>
              </motion.div>
            </ScrollReveal>
            <div className="flow-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
            <ScrollReveal delay={0.2}>
              <motion.div className="flow-step" whileHover={{ y: -5 }}>
                <span className="step-icon">{Icons.radio}</span>
                <span className="step-title">LoRa Gateway</span>
                <span className="step-desc">Mesh networking</span>
              </motion.div>
            </ScrollReveal>
            <div className="flow-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
            <ScrollReveal delay={0.3}>
              <motion.div className="flow-step" whileHover={{ y: -5 }}>
                <span className="step-icon">{Icons.sun}</span>
                <span className="step-title">AI4Solar</span>
                <span className="step-desc">Analysis & alerts</span>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Notify CTA */}
      <section className="draft-cta sensor-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Get Notified When Available</h2>
            <p>
              Be the first to know when the SMM Sensor Box is ready for order.
              Early subscribers get priority access and exclusive pricing.
            </p>

            {!submitted ? (
              <form className="notify-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="notify-input"
                  />
                  <GlowButton type="submit" variant="solar" size="large">
                    Notify Me
                  </GlowButton>
                </div>
                <p className="form-note">
                  We respect your privacy. Unsubscribe anytime.
                </p>
              </form>
            ) : (
              <motion.div
                className="success-message"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <span className="success-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </span>
                <h3>You're on the list!</h3>
                <p>We'll notify you as soon as the SMM Sensor Box is available.</p>
              </motion.div>
            )}
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
